<template>
  <loader :is-loading="isPending || optionsLoading" />
  <div class="flex h-full flex-col">
    <!-- Settings page header -->
    <div class="flex items-center justify-between gap-6 p-6">
      <span v-if="compact" class="text-sm font-semibold text-headline">
        {{ $t("SHORTCUTS.TITLE_LONG", 2) }}
      </span>
      <div v-else class="flex flex-1 flex-col gap-1">
        <span class="text-xl font-semibold text-headline">
          {{ $t("SHORTCUTS.TITLE_LONG", 2) }}
        </span>
        <div class="flex items-center gap-2">
          <shortcut-key-badges :shortcut="helpShortcut" />
          <span class="text-sm text-label">
            {{ $t("SHORTCUTS.OPEN_PANEL_HINT") }}
          </span>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="flex size-10 items-center justify-center rounded bg-white disabled:opacity-50"
          :disabled="!hasCustomShortcuts"
          @click="showResetAllModal = true"
        >
          <icon-base
            :icon="IconRestore"
            width="16"
            height="16"
            view-box="0 0 15 14"
            class="text-label"
          />
        </button>
        <search-input
          v-model="searchQuery"
          :delay="300"
          search-term=""
          :no-margin="true"
          class="w-50 lg:w-60"
        />
      </div>
    </div>
    <!-- Content -->
    <div
      :class="[
        compact
          ? 'flex-1 overflow-y-auto px-4 pb-4'
          : 'flex flex-col gap-6 px-6 pb-6',
        {
          'flex flex-col flex-1 items-center justify-center':
            searchQuery && !filteredGroups.length,
        },
      ]"
    >
      <template v-if="filteredGroups.length">
        <!-- Compact: flat list -->
        <template v-if="compact">
          <template v-for="group in filteredGroups" :key="group.context">
            <shortcut-group-header :label="group.context" />
            <shortcut-list-item
              v-for="shortcut in group.shortcuts"
              :key="shortcut.key"
              :shortcut="shortcut"
              editable
              @save="initSaveChanges"
              @reset="deleteShortcut"
            />
          </template>
        </template>
        <!-- Settings page: responsive grid of cards -->
        <template v-else>
          <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div
              v-for="group in filteredGroups"
              :key="group.context"
              class="h-fit rounded-md border border-divider bg-white"
            >
              <shortcut-group-header :label="group.context" />
              <shortcut-list-item
                v-for="(shortcut, index) in group.shortcuts"
                :key="shortcut.key"
                :shortcut="shortcut"
                editable
                :is-last="index === group.shortcuts.length - 1"
                @save="initSaveChanges"
                @reset="deleteShortcut"
              />
            </div>
          </div>
        </template>
      </template>
      <div v-else-if="searchQuery" class="flex flex-col items-center gap-4">
        <div
          class="flex size-19 items-center justify-center rounded-full bg-ice-blue"
        >
          <icon-base
            :icon="IconSearch"
            width="28"
            height="28"
            view-box="0 0 18 18"
            fill="#8497C2"
          />
        </div>
        <span class="text-sm font-semibold text-headline">
          {{ $t("SHORTCUTS.NO_MATCHING_SHORTCUTS") }}
        </span>
      </div>
    </div>
  </div>
  <confirm-modal
    v-if="showConfirmModal"
    :title="$t('SHORTCUTS.CONFLICT_TITLE')"
    :description="$t('SHORTCUTS.CONFLICT_DESCRIPTION')"
    :confirm-button="$t('COMMON.SAVE')"
    confirm-button-type="success"
    :close="() => (showConfirmModal = false)"
    @confirm="saveChanges(pendingUpdate!)"
  />
  <confirm-modal
    v-if="showResetAllModal"
    :title="$t('SHORTCUTS.RESET_ALL_TITLE')"
    :description="$t('SHORTCUTS.RESET_ALL_DESCRIPTION')"
    :confirm-button="$t('COMMON.CONFIRM')"
    confirm-button-type="error"
    :close="() => (showResetAllModal = false)"
    @confirm="resetAllShortcuts"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import { AnalyticsEvent } from "@/enums/analytics";
import { ShortcutAction, ShortcutContext } from "@/enums/keyboardShortcuts";
import { sleep } from "@/helpers/common";
import { usePromiseWrapper } from "@/hooks/common";
import {
  useCustomizeKeyboardShortcuts,
  useKeyboardShortcuts,
} from "@/hooks/keyboardShortcuts";
import { useNotification } from "@/hooks/notifications";
import { useUpdateKeyboardShortcutMutation } from "@/hooks/queries/useKeyboardShortcuts";
import { ROUTE_DEALS, ROUTE_INBOX } from "@/router/route-names";
import { useAiAgentsStore } from "@/stores/ai/agents";
import { useAnalyticsStore } from "@/stores/analytics";

import IconSearch from "@/components/icons/IconSearch.vue";
import IconRestore from "@/components/icons/shortcuts/IconRestore.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import ShortcutGroupHeader from "@/components/shortcuts/ShortcutGroupHeader.vue";
import ShortcutKeyBadges from "@/components/shortcuts/ShortcutKeyBadges.vue";
import ShortcutListItem from "@/components/shortcuts/ShortcutListItem.vue";
import IconBase from "@/components/ui/IconBase.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";

import type {
  KeyboardShortcutPayload,
  KeyboardShortcutPayloadEntry,
  KeyboardShortcutUpdate,
  UserKeyboardShortcut,
} from "@/models/options";

const { compact = false } = defineProps<{
  compact?: boolean;
}>();

const {
  configuredShortcuts,
  groupedShortcuts,
  formatActionName,
  getShortcutForAction,
} = useKeyboardShortcuts();
const { showMessage } = useNotification();
const { dispatch } = useStore();

const { mapModifiers, mapConfiguredShortcuts } =
  useCustomizeKeyboardShortcuts();
const { trackEvent } = useAnalyticsStore();
const { t } = useI18n();
const { mutateAsync, isPending } = useUpdateKeyboardShortcutMutation();
const { agentSessionInfo } = storeToRefs(useAiAgentsStore());

const searchQuery = ref("");
const showConfirmModal = ref(false);
const showResetAllModal = ref(false);
const pendingUpdate = ref<KeyboardShortcutUpdate | null>(null);
const hasCustomShortcuts = computed(() =>
  configuredShortcuts.value.some(
    (sc) =>
      sc.is_custom || !Object.prototype.hasOwnProperty.call(sc, "is_custom")
  )
);

const helpShortcut = computed(
  () =>
    getShortcutForAction(ShortcutAction.ToggleHelp) ??
    ({
      modifiers: [],
      keys: ["/"],
    } satisfies Partial<UserKeyboardShortcut>)
);

const route = useRoute();

const priorityContext = computed<ShortcutContext | null>(() => {
  if (!compact) {
    return null;
  }
  const name = route.name as string | undefined;
  if (!name) {
    return null;
  }
  if (name === ROUTE_DEALS) {
    return ShortcutContext.DealsList;
  }
  if (name === ROUTE_INBOX || name.startsWith(ROUTE_INBOX)) {
    return ShortcutContext.Inbox;
  }
  if (name.startsWith("Deal") && name !== ROUTE_DEALS) {
    return ShortcutContext.ActiveDeal;
  }
  return null;
});

const sortedGroups = computed(() => {
  const groups = groupedShortcuts.value;
  const ctx = priorityContext.value;
  if (!ctx) {
    return groups;
  }
  const priority = groups.find((g) => g.contextKey === ctx);
  if (!priority) {
    return groups;
  }
  return [priority, ...groups.filter((g) => g.contextKey !== ctx)];
});

const filteredGroups = computed(() => {
  const query = searchQuery.value.toLowerCase();
  if (!query) {
    return sortedGroups.value;
  }
  return sortedGroups.value
    .map((group) => ({
      ...group,
      shortcuts: group.shortcuts.filter((sc) =>
        formatActionName(sc.action_name).toLowerCase().includes(query)
      ),
    }))
    .filter((group) => group.shortcuts.length > 0);
});

const initSaveChanges = (update: KeyboardShortcutUpdate) => {
  if (!update.valid) {
    showMessage(
      t("SHORTCUTS.INVALID_SHORTCUT", { shortcut: update.action }),
      "error"
    );
    return;
  }
  if (update.conflict) {
    pendingUpdate.value = update;
    showConfirmModal.value = true;
    return;
  }
  saveChanges(update);
};

const saveChanges = async (update: KeyboardShortcutUpdate) => {
  const shortcut = configuredShortcuts.value.find(
    (sc) => sc.key === update.key
  );
  if (!shortcut) {
    return;
  }

  const { action_name, context } = shortcut;
  const { key, action, action_string, modifiers, keys } = update;

  const mappedModifiers = mapModifiers(modifiers ?? []);
  const configuredShortcutsMap = mapConfiguredShortcuts(
    configuredShortcuts.value
  );

  const updatePayload: KeyboardShortcutPayload = {
    shortcuts: {
      ...configuredShortcutsMap,
      [key]: {
        action,
        action_string,
        modifiers: mappedModifiers,
        keys,
        action_name,
        context,
      },
    },
  };

  try {
    await mutateAsync(updatePayload);
    trackEvent(AnalyticsEvent.KeyboardShortcutCustomized, {
      action_name,
      context,
      new_shortcut: action,
      new_action_string: action_string,
    });
    showMessage(
      t("SHORTCUTS.SUCCESSFULLY_REASSIGNED", {
        action: formatActionName(action_name),
      }),
      "success"
    );
    showConfirmModal.value = false;
    pendingUpdate.value = null;
    await getOptions();
  } catch {
    showMessage(t("COMMON.ERROR"), "error");
  }
};

const deleteShortcut = async (shortcut: UserKeyboardShortcut) => {
  const { key, action, action_name, action_string, modifiers, keys, context } =
    shortcut;
  const configuredShortcutsMap = mapConfiguredShortcuts(
    configuredShortcuts.value
  );

  const deletePayload = {
    shortcuts: {
      ...configuredShortcutsMap,
      [key]: {
        action,
        action_name,
        action_string,
        modifiers,
        keys,
        context,
        reset: 1,
      },
    },
  };
  try {
    await mutateAsync(deletePayload);
    trackEvent(AnalyticsEvent.KeyboardShortcutReset, {
      action_name,
      context,
    });
    showMessage(t("SHORTCUTS.SUCCESSFULLY_RESTORED"), "success");
    await getOptions();
  } catch {
    showMessage(t("COMMON.ERROR"), "error");
  }
};

const resetAllShortcuts = async () => {
  showResetAllModal.value = false;

  const customShortcuts = configuredShortcuts.value.filter(
    (sc) =>
      sc.is_custom || !Object.prototype.hasOwnProperty.call(sc, "is_custom")
  );
  if (!customShortcuts.length) {
    return;
  }

  const configuredShortcutsMap = mapConfiguredShortcuts(
    configuredShortcuts.value
  );

  const resetEntries = customShortcuts.reduce<
    Record<string, KeyboardShortcutPayloadEntry>
  >((acc, sc) => {
    acc[sc.key] = {
      action: sc.action,
      action_name: sc.action_name,
      action_string: sc.action_string,
      modifiers: sc.modifiers,
      keys: sc.keys,
      context: sc.context,
      reset: 1,
    };
    return acc;
  }, {});

  try {
    await mutateAsync({
      shortcuts: {
        ...configuredShortcutsMap,
        ...resetEntries,
      },
    });
    trackEvent(AnalyticsEvent.KeyboardShortcutReset, {
      action_name: "all",
      context: "all",
    });
    showMessage(t("SHORTCUTS.SUCCESSFULLY_RESTORED"), "success");
    await getOptions();
  } catch {
    showMessage(t("COMMON.ERROR"), "error");
  }
};

const { fetchWrapper: getOptions, loading: optionsLoading } = usePromiseWrapper(
  async () => {
    await dispatch("options/getAll");
  }
);

watch([showConfirmModal, showResetAllModal], async ([confirm, resetAll]) => {
  if (!compact) {
    return;
  }
  if (!confirm && !resetAll) {
    await sleep(100);
    agentSessionInfo.value.hasActiveOverlay = false;
    return;
  }
  agentSessionInfo.value.hasActiveOverlay = true;
});
</script>
